import onetime from "@-/com.npmjs.onetime";
import { useLayoutEffect, useSyncExternalStore } from "react";

type ScrollerRegistry = { listeners: Set<() => void>; revision: number };

const getRegistry = onetime((): ScrollerRegistry => ({
  listeners: new Set<() => void>(),
  revision: 0,
}));

function bumpRevision() {
  const registry = getRegistry();
  registry.revision += 1;
  for (const listener of registry.listeners) {
    listener();
  }
}

function subscribe(listener: () => void) {
  const { listeners } = getRegistry();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getRevision() {
  return getRegistry().revision;
}

/**
 * Called by every page scroller that chrome may float over. Its mount and unmount re-target each
 * `ScrollEdgeElementContainer`: a container usually outlives the scroller beneath it (a suspense
 * fallback replaced by the loaded list, a list remounted for another filter).
 */
export function useScrollEdgeScrollerRegistration(enabled = true) {
  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }
    bumpRevision();
    return bumpRevision;
  }, [enabled]);
}

/** Changes whenever a registered page scroller mounts or unmounts. */
export function useScrollEdgeScrollersRevision() {
  return useSyncExternalStore(subscribe, getRevision, getRevision);
}
